<template>
  <div>
    <v-slide-y-reverse-transition>
      <v-card v-if="selected.length > 0" class="floating-actions-bar elevation-4">
        <v-card-text class="py-3">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-progress-circular
                v-if="bulkUpdating"
                indeterminate
                size="20"
                width="2"
                color="primary"
                class="mr-2"
              ></v-progress-circular>
              <v-icon v-else class="mr-2" color="primary">mdi-checkbox-marked-circle</v-icon>
              <span class="font-weight-medium text--primary">
                {{ bulkUpdating ? 'Updating...' : `${selected.length} record(s) selected` }}
              </span>
              <!-- <v-btn
                v-if="showSelectAll && selected.length < totalItems"
                small
                text
                color="primary"
                @click="selectAll"
                class="ml-4"
                :disabled="bulkUpdating"
                style="text-transform: none"
              >
                Select All ({{ totalItems }})
              </v-btn> -->
            </div>
            <div>
              <!-- Dynamic Buttons -->
              <template v-for="action in visibleActions">
                <!-- Regular Button -->
                <v-btn
                  v-if="!action.menuItems"
                  :key="'btn-' + action.id"
                  small
                  :color="action.color || 'primary'"
                  @click="handleButtonClick(action)"
                  :disabled="
                    action.disabled ||
                    bulkUpdating ||
                    (action.requiresSelection && selected.length === 0)
                  "
                  class="mr-2"
                  style="text-transform: none"
                >
                  <v-icon v-if="action.icon" left small>{{ action.icon }}</v-icon>
                  {{ action.label }}
                </v-btn>

                <!-- Menu Button -->
                <v-menu
                  v-else
                  :key="'menu-' + action.id"
                  offset-y
                  top
                  :disabled="action.disabled || bulkUpdating"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      :color="action.color || 'primary'"
                      v-bind="attrs"
                      v-on="on"
                      class="mr-2"
                      :disabled="action.disabled || bulkUpdating"
                      style="text-transform: none"
                    >
                      <v-icon v-if="action.icon" left small>{{ action.icon }}</v-icon>
                      {{ action.label }}
                    </v-btn>
                  </template>
                  <v-list color="white" dense class="pa-0">
                    <v-list-item
                      v-for="menuItem in action.menuItems"
                      :key="menuItem.value || menuItem.id"
                      @click="handleMenuItemClick(action, menuItem)"
                      class="px-3 py-1"
                      dense
                    >
                      <v-list-item-title class="text-caption" style="text-transform: none">{{
                        menuItem.text || menuItem.label
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-slide-y-reverse-transition>
  </div>
</template>

<script>
export default {
  name: 'FloatingActionBar',
  props: {
    selected: {
      type: Array,
      default: () => []
    },
    totalItems: {
      type: Number,
      default: 0
    },
    bulkUpdating: {
      type: Boolean,
      default: false
    },
    statusOptions: {
      type: Array,
      default: () => []
    },
    // New dynamic actions prop
    actions: {
      type: Array,
      default: () => [],
      validator: (actions) => {
        return actions.every(
          (action) => action.id && action.label && typeof action.show !== 'undefined'
        )
      }
    },
    // Legacy props for backward compatibility
    showActiveSelected: {
      type: Boolean,
      default: false
    },
    showDeleteAll: {
      type: Boolean,
      default: false
    },
    showUpdateStatus: {
      type: Boolean,
      default: false
    },
    showClear: {
      type: Boolean,
      default: true
    },
    showSelectAll: {
      type: Boolean,
      default: true
    },
    showAssign: {
      type: Boolean,
      default: true
    },
    showBranch: {
      type: Boolean,
      default: false
    },
    showUpdate: {
      type: Boolean,
      default: false
    },
    showUpdateOutcome: {
      type: Boolean,
      default: false
    },
    showAssignSelection: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    /**
     * Computed property that merges legacy props with new actions array
     * Returns array of visible actions based on configuration
     */
    visibleActions() {
      // If actions prop is provided, use it
      if (this.actions && this.actions.length > 0) {
        return this.actions.filter((action) => action.show)
      }

      // Fallback to legacy props for backward compatibility
      const legacyActions = []

      // Always show Assign Location option
      legacyActions.push({
        id: 'assignLocation',
        label: 'Assign Location',
        icon: 'mdi-map-marker',
        color: 'primary',
        event: 'assign-location',
        show: true,
        requiresSelection: false
      })

      // Add Assign Department option
      legacyActions.push({
        id: 'assignDepartment',
        label: 'Assign Department',
        icon: 'mdi-office-building',
        color: 'primary',
        event: 'assign-department',
        show: true,
        requiresSelection: false
      })
      if (this.showUpdateOutcome) {
        legacyActions.push({
          id: 'updateOutcome',
          label: 'Update Outcome',
          icon: 'mdi-update',
          color: 'primary',
          event: 'update-outcome',
          show: true,
          requiresSelection: true
        })
      }
      if (this.showAssignSelection) {
        legacyActions.push({
          id: 'assignCases',
          label: 'Assign Cases',
          icon: 'mdi-account-multiple',
          color: 'primary',
          event: 'assign-cases',
          show: true,
          requiresSelection: true
        })
      }

      if (this.showUpdateStatus) {
        legacyActions.push({
          id: 'updateStatus',
          label: 'Update Status',
          icon: 'mdi-update',
          color: 'primary',
          event: 'bulk-status-update',
          show: true,
          requiresSelection: false,
          menuItems: this.statusOptions
        })
      }
      if (this.showActiveSelected) {
        legacyActions.push({
          id: 'activeInactive',
          label: 'Update Status',
          icon: 'mdi-toggle-switch',
          color: 'primary',
          event: 'toggle-active-inactive',
          show: true,
          requiresSelection: true,
          menuItems: [
            { text: 'Active', value: 'ACTIVE', id: 'markActive', class: 'font-size-large' },
            { text: 'Inactive', value: 'INACTIVE', id: 'markInactive', class: 'font-size-large' }
          ]
        })
      }

      if (this.showDeleteAll) {
        legacyActions.push({
          id: 'deleteAll',
          label: 'Delete All',
          icon: 'mdi-delete-sweep',
          color: 'primary',
          event: 'delete-all',
          show: true,
          requiresSelection: false
        })
      }

      return legacyActions
    }
  },
  methods: {
    /**
     * Handle button click events
     * @param {Object} action - Action configuration object
     */
    handleButtonClick(action) {
      this.$emit(action.event, action)
    },

    /**
     * Handle menu item click events
     * @param {Object} action - Parent action configuration
     * @param {Object} menuItem - Selected menu item
     */
    handleMenuItemClick(action, menuItem) {
      if (action.event === 'bulk-status-update') {
        this.$emit('bulk-status-update', menuItem.value)
      } else {
        this.$emit(action.event, menuItem)
      }
    },

    /**
     * Legacy method for bulk status update
     * @param {String} status - Status value
     */
    handleBulkStatusUpdate(status) {
      this.$emit('bulk-status-update', status)
    },

    /**
     * Handle select all functionality
     */
    selectAll() {
      this.$emit('select-all')
    }
  }
}
</script>
<style scoped>
.floating-actions-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  min-width: 60vw;
  max-width: 70vw;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  background-color: rgb(222, 221, 221);
}

/* Global style to ensure all buttons in this component have normal case text */
.floating-actions-bar :deep(.v-btn) {
  text-transform: none !important;
}

.floating-actions-bar :deep(.v-list-item-title) {
  text-transform: none !important;
}
</style>
